import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { useT } from "@/i18n";
import { INSIGHTS } from "@/data/content";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "id" ? "id-ID" : "en-GB", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function InsightDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const post = INSIGHTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (post) document.title = `${post.title[lang] || post.title.id} — Makario`;
  }, [post, lang]);

  useEffect(() => {
    if (!post) return;
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='in-meta']", { opacity: 0, y: 20, duration: 0.7 });
      gsap.from("[data-anim='in-title']", { opacity: 0, y: 50, duration: 1.1, ease: "power3.out", delay: 0.15 });
      gsap.from("[data-anim='in-issue']", { opacity: 0, scale: 0.96, duration: 1.4, ease: "power3.out" });
      gsap.from("[data-anim='in-para']", {
        opacity: 0, y: 30, duration: 0.8, stagger: 0.1,
        scrollTrigger: { trigger: "[data-anim='in-body']", start: "top 80%" },
      });
    });
    return () => ctx.revert();
  }, [post]);

  if (!post) {
    return (
      <main className="min-h-screen flex items-center justify-center text-[var(--ink)]">
        <div className="text-center">
          <p className="h-sub">{t("detail.notFound")}</p>
          <button onClick={() => navigate("/")} className="btn-pill mt-8 text-[var(--ink)] border-[var(--ink)]">
            <ArrowLeft size={14} /> {t("detail.back")}
          </button>
        </div>
      </main>
    );
  }

  // Issue number from order in INSIGHTS array
  const insightIdx = INSIGHTS.findIndex((p) => p.slug === post.slug);
  const issueNo = String(insightIdx + 1).padStart(3, "0");

  return (
    <main data-testid="insight-detail" className="min-h-screen">
      <Navbar />

      {/* HERO — magazine cover */}
      <section className="relative min-h-screen flex flex-col px-8 md:px-16 lg:px-24 pt-36 md:pt-32 pb-12 overflow-hidden grain" style={{ background: "var(--ink)" }}>
        <div
          className="absolute pointer-events-none rounded-full"
          style={{
            width: 900, height: 900, top: "-25%", right: "-15%",
            background: `radial-gradient(circle, var(--violet) 0%, transparent 60%)`,
            opacity: 0.14, filter: "blur(40px)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(90deg, transparent calc(100% - 1px), white calc(100% - 1px))",
            backgroundSize: "12.5% 100%",
          }}
        />

        {/* Top eyebrow row */}
        <div className="relative z-10 flex items-center justify-between mb-12 md:mb-14">
          <Link
            to="/#insights"
            data-testid="in-back"
            className="inline-flex items-center gap-2 text-white/65 hover:text-white text-[0.65rem] font-mono uppercase tracking-[0.28em]"
          >
            <ArrowLeft size={14} /> {t("insights.back")}
          </Link>
          <div className="flex items-center gap-6">
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {lang === "id" ? "Edisi" : "Issue"} · {issueNo}
            </span>
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-white/45">
              {t("insights.eyebrow") || "Insights"}
            </span>
          </div>
        </div>

        {/* Main grid */}
        <div className="relative z-10 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT — Title + meta */}
          <div className="lg:col-span-8 text-white">
            <div data-anim="in-meta" className="flex flex-wrap items-center gap-3 mb-7">
              <span className="text-[0.6rem] font-mono uppercase tracking-[0.28em] px-2.5 py-1 rounded-sm bg-[var(--violet)] text-white">
                {post.category[lang]}
              </span>
              <span className="text-[0.6rem] font-mono uppercase tracking-[0.28em] text-white/55">
                {formatDate(post.date, lang)}
              </span>
              <span className="text-[0.6rem] font-mono uppercase tracking-[0.28em] text-white/55">
                {post.readingTime} {t("insights.readingTime")}
              </span>
            </div>

            <h1
              data-anim="in-title"
              data-testid="in-title"
              className="font-display text-white"
              style={{
                fontSize: "clamp(2.2rem, 5.4vw, 4.6rem)",
                lineHeight: 1,
                letterSpacing: "-0.03em",
                fontWeight: 700,
              }}
            >
              {post.title[lang]}
            </h1>
            <p className="mt-10 max-w-[36rem] text-white/65 text-[15px] leading-relaxed font-display italic" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {post.excerpt && post.excerpt[lang] ? post.excerpt[lang] : ""}
            </p>
          </div>

          {/* RIGHT — Issue stamp */}
          <div className="lg:col-span-4 flex items-center justify-end">
            <div data-anim="in-issue" className="relative">
              <div
                className="font-display select-none leading-none"
                style={{
                  fontSize: "clamp(7rem, 16vw, 14rem)",
                  fontWeight: 800,
                  letterSpacing: "-0.06em",
                  color: "var(--violet)",
                  opacity: 0.20,
                  textShadow: "0 0 80px rgba(91,75,255,0.35)",
                }}
              >
                №{issueNo}
              </div>
              <div className="absolute -bottom-2 right-0 text-right">
                <div className="font-mono text-[0.55rem] tracking-[0.3em] uppercase text-white/40">
                  Makario Insights
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="relative z-10 mt-10 pt-5 border-t border-white/10 flex items-center justify-between font-mono text-[0.6rem] uppercase tracking-[0.28em] text-white/35">
          <span>{lang === "id" ? "Catatan dari studio kami" : "Notes from our studio"}</span>
          <span>№{issueNo} · {formatDate(post.date, lang)}</span>
        </div>
      </section>

      {/* BODY — long-form */}
      <section
        data-anim="in-body"
        className="relative px-6 sm:px-8 md:px-16 lg:px-24 py-24 md:py-36"
        style={{ background: "var(--paper)" }}
      >
        <div className="max-w-[1240px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Side ledger */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32 space-y-6 text-sm">
              <div>
                <div className="font-mono text-[0.55rem] uppercase tracking-[0.3em] text-black/40 mb-2">
                  {lang === "id" ? "Kategori" : "Category"}
                </div>
                <div className="font-display text-[var(--ink)] text-base font-semibold">
                  {post.category[lang]}
                </div>
              </div>
              <div>
                <div className="font-mono text-[0.55rem] uppercase tracking-[0.3em] text-black/40 mb-2">
                  {lang === "id" ? "Diterbitkan" : "Published"}
                </div>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] text-black/65">
                  {formatDate(post.date, lang)}
                </div>
              </div>
              <div>
                <div className="font-mono text-[0.55rem] uppercase tracking-[0.3em] text-black/40 mb-2">
                  {lang === "id" ? "Waktu Baca" : "Reading Time"}
                </div>
                <div className="font-mono text-[0.7rem] tracking-[0.18em] text-black/65">
                  {post.readingTime} {t("insights.readingTime")}
                </div>
              </div>
              <div className="pt-4 border-t border-black/10">
                <div className="font-mono text-[0.55rem] uppercase tracking-[0.3em] text-black/40 mb-2">
                  №{issueNo}
                </div>
              </div>
            </div>
          </aside>

          {/* Article column */}
          <article className="lg:col-span-9">
            <div className="max-w-[44rem] space-y-7">
              {post.body[lang].map((para, i) => (
                <p
                  key={i}
                  data-anim="in-para"
                  className={
                    i === 0
                      ? "text-[var(--ink)] font-display"
                      : "text-black/75 text-[16px] leading-[1.75]"
                  }
                  style={
                    i === 0
                      ? {
                          fontSize: "clamp(1.25rem, 1.9vw, 1.55rem)",
                          lineHeight: 1.4,
                          fontWeight: 500,
                          letterSpacing: "-0.005em",
                        }
                      : {}
                  }
                >
                  {i === 0 ? (
                    <>
                      <span
                        className="float-left mr-3 font-display"
                        style={{
                          fontSize: "clamp(3.6rem, 5vw, 4.6rem)",
                          fontWeight: 800,
                          lineHeight: 0.82,
                          color: "var(--violet)",
                          letterSpacing: "-0.04em",
                        }}
                      >
                        {para.charAt(0)}
                      </span>
                      {para.slice(1)}
                    </>
                  ) : (
                    para
                  )}
                </p>
              ))}

              <div className="pt-14 mt-14 border-t border-black/10 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
                <Link to="/#insights" className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.28em] text-[var(--ink)] hover:text-[var(--violet)] transition-colors">
                  <ArrowLeft size={14} /> {t("insights.back")}
                </Link>
                <Link to="/#contact" className="btn-pill btn-pill-violet">
                  {t("nav.cta")} <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </article>
        </div>

        {/* Related insights */}
        {INSIGHTS.filter((p) => p.slug !== post.slug).length > 0 && (
          <div className="max-w-[1240px] mx-auto mt-32 pt-12 border-t border-black/10">
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.32em] text-black/40 mb-10">
              {lang === "id" ? "Edisi Lainnya" : "Other Issues"}
            </div>
            <ol className="border-t border-black/10">
              {INSIGHTS.filter((p) => p.slug !== post.slug).map((p) => {
                const idx = INSIGHTS.findIndex((q) => q.slug === p.slug);
                return (
                  <li key={p.slug} className="border-b border-black/10">
                    <Link
                      to={`/insights/${p.slug}`}
                      className="group flex items-center justify-between gap-6 py-5"
                      data-testid={`in-related-${p.slug}`}
                    >
                      <div className="flex items-baseline gap-6 min-w-0">
                        <span className="font-mono text-[0.6rem] uppercase tracking-[0.28em] text-[var(--violet)] flex-none">
                          №{String(idx + 1).padStart(3, "0")}
                        </span>
                        <span
                          className="font-display text-[var(--ink)] truncate transition-all duration-300 group-hover:translate-x-1"
                          style={{ fontSize: "clamp(1.2rem,2.1vw,1.7rem)", letterSpacing: "-0.02em", fontWeight: 600 }}
                        >
                          {p.title[lang]}
                        </span>
                      </div>
                      <ArrowUpRight size={18} className="text-black/35 group-hover:text-[var(--violet)] transition-colors flex-none" />
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </section>

      <Footer />
    </main>
  );
}
